/**
 * clickable-revehicle.js -- THE owner of the vehicle-transition dim and the
 * bfcache revalidation. One mechanism, one owner, two entry points
 * (Lucas brief, 20 Aug):
 *
 *   1. TRANSITION DIM. When a vehicle is set/changed on a collection or
 *      search grid AND a scoped navigation will follow, the outgoing grid
 *      must never light up with wrong-vehicle warnings in the beat before
 *      the browser leaves. begin() dims every grid container
 *      (#ProductGridContainer -> .is-revehicling: opacity .5,
 *      pointer-events none, 150ms) and raises a suppress flag;
 *      clickable-card-fitment.js reads isActive() and skips its per-card
 *      repaint while the flag is up. The class rides the OLD page to its
 *      unload; a 5s failsafe clears it if the navigation never happens.
 *      begin() is called from the navigation OWNERS (the selector's
 *      completeModel, the EasySearch state-mirror in clickable-vehicle-
 *      state.js) BEFORE the state write, so the flag is already up when
 *      'clickable:vehicle-changed' fires synchronously inside setVehicle --
 *      no listener-registration-order race.
 *
 *   2. BFCACHE REVALIDATION. On pageshow with persisted === true the page's
 *      painted state is frozen from before it was cached; the vehicle or
 *      the cart may have changed in another tab since. A server-rendered
 *      vehicle-scoped page (a /collections URL carrying a vehicle tag)
 *      whose scope no longer matches the stored vehicle takes the SAME dim
 *      and a location.reload(); every other page re-syncs IN PLACE -- the
 *      vehicle surfaces via clickableVehicleState.revalidate() (re-emits
 *      vehicle-changed; every subscriber's repaint is idempotent) and the
 *      cart paint via 'clickable:cart-revalidate' (ADDED button + Added
 *      chips re-fetch /cart.js through their own modules).
 *
 * DESKTOP: the dim is intentionally NOT width-fenced -- it applies at all
 * widths (Lucas brief: "desktop shares the JS ... unless I say fence it").
 * FLAGGED in the session report. The CSS lives in clickable-tokens.css.
 */
(function () {
  'use strict';

  var GRID_SEL = '#ProductGridContainer';
  var DIM_CLASS = 'is-revehicling';
  var FAILSAFE_MS = 5000;
  var active = false;
  var failsafeTimer = null;

  function grids() {
    return document.querySelectorAll(GRID_SEL);
  }

  // Raise the dim + suppress flag. Idempotent: a second call just re-arms
  // the failsafe. No grid on the page -> nothing to dim (homepage, header,
  // PDP), so the flag stays down and card-fitment is never suppressed.
  function begin() {
    var list = grids();
    if (!list.length) return;
    active = true;
    for (var i = 0; i < list.length; i++) list[i].classList.add(DIM_CLASS);
    if (failsafeTimer) clearTimeout(failsafeTimer);
    failsafeTimer = setTimeout(clear, FAILSAFE_MS);
  }

  // Lower the dim. Called by the failsafe ONLY (a real navigation unloads
  // the page before this fires). Because the repaint was suppressed while
  // active, a failsafe clear leaves the cards stale -> reconcile them to
  // the now-current vehicle with a repaint signal.
  function clear() {
    if (failsafeTimer) { clearTimeout(failsafeTimer); failsafeTimer = null; }
    var wasActive = active;
    active = false;
    var list = grids();
    for (var i = 0; i < list.length; i++) list[i].classList.remove(DIM_CLASS);
    if (wasActive) {
      document.dispatchEvent(new CustomEvent('clickable:cards-added'));
    }
  }

  window.clickableRevehicle = {
    begin: begin,
    clear: clear,
    isActive: function () { return active; }
  };

  // ---- bfcache leg -----------------------------------------------------
  function VS() { return window.clickableVehicleState; }

  // Read-only: the canonical vehicle tag this /collections URL is server-
  // scoped to, or null when the URL carries no vehicle tag (unscoped grid,
  // category-only tag, or any non-collection page). Never writes state.
  function urlVehicleTag() {
    var vs = VS();
    if (!vs || !vs.resolveTag) return null;
    var segs = window.location.pathname.split('/').filter(Boolean);
    if (segs[0] !== 'collections' || segs.length < 3) return null;
    var last;
    try { last = decodeURIComponent(segs[segs.length - 1]); }
    catch (e) { last = segs[segs.length - 1]; }
    var parts = last.split('+');
    for (var i = 0; i < parts.length; i++) {
      var name = vs.resolveTag(parts[i]);
      if (name) return vs.toTag(name);
    }
    return null;
  }

  /* BFCACHE NO-OP CONTRACT (Lucas, 21 Aug — swipe-back flash): the frozen
     paint IS current unless state moved while the page slept. Snapshot the
     vehicle signature at freeze (pagehide.persisted); on restore, when the
     signature is unchanged the vehicle re-emit is SKIPPED entirely — the
     re-sync touches no classes and no DOM. Cart revalidation stays (its
     consumers fetch truth and repaint by value — paint-neutral when
     nothing changed). */
  function vehicleSig() {
    var vs = VS();
    if (!vs || !vs.getTag) return null;
    var ctx = vs.getContextTag ? (vs.getContextTag() || '') : '';
    return (vs.getTag() || '') + '|' + ctx;
  }

  /* BFCACHE-SAFE NO-OP CONTRACT (Lucas, 21 Aug — back-nav regression fix).
     REPLACES the b96 `pagehide` snapshot: NO listener does work in the
     pagehide window any more (Safari is strict about the freeze window,
     and the pagehide handler was the prime bfcache-eligibility suspect).
     Instead `displayedSig` records what the page currently SHOWS — captured
     at load and updated whenever the vehicle changes on THIS page or a
     restore reconciles it. It is a plain variable, so bfcache freezes and
     restores it with the rest of the heap; on restore we compare the
     CURRENT (fresh localStorage) signature against it and revalidate ONLY
     when they differ. Same no-op guarantee, zero pagehide work. */
  var displayedSig = vehicleSig();
  document.addEventListener('clickable:vehicle-changed', function () {
    displayedSig = vehicleSig();
  });

  function onPageshow(event) {
    if (!event.persisted) return; // only bfcache restores need reconciling
    var vs = VS();
    var scopeTag = urlVehicleTag();
    // Server-rendered vehicle-scoped page whose scope no longer matches the
    // stored vehicle -> the grid the SERVER tag-filtered is stale; dim and
    // reload. (Reload re-hydrates the vehicle FROM THE URL -- the URL wins,
    // consistent with clickable-vehicle-state.js hydration. FLAGGED.)
    if (scopeTag && vs && scopeTag !== vs.getTag()) {
      begin();
      window.location.reload();
      return;
    }
    // Light page (or a scoped page still in scope): re-sync in place, no
    // reload — but ONLY when the vehicle actually moved while frozen; an
    // unchanged signature re-emits nothing and repaints nothing.
    var sig = vehicleSig();
    if (vs && vs.revalidate && sig !== displayedSig) vs.revalidate();
    displayedSig = sig; // the page now reflects the current state
    document.dispatchEvent(new CustomEvent('clickable:cart-revalidate'));
  }

  window.addEventListener('pageshow', onPageshow);
})();
